import React, { useMemo } from 'react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { ConvergenceBadge } from '../components/primitives';

// Force convergence plot. The PowerFLOW monitors record BODY-axis Z/X forces
// (automotive default); the backend rotates them into WIND axes by the case
// angle of attack so these are true aircraft lift/drag. Flattening curves =
// converged run → the engineer can stop early.
export function ConvergenceChart({ convergence }) {
  const data = useMemo(() => {
    const lift = convergence?.lift || [];
    const drag = convergence?.drag || [];
    if (lift.length === 0 && drag.length === 0) return [];
    const byTs = new Map();
    lift.forEach(([ts, v]) => { byTs.set(ts, { ts, lift: v }); });
    drag.forEach(([ts, v]) => { byTs.set(ts, { ...(byTs.get(ts) || { ts }), drag: v }); });
    return Array.from(byTs.values()).sort((a, b) => a.ts - b.ts);
  }, [convergence]);

  if (data.length < 2) return null;
  const aoa = convergence?.aoaDeg;
  const axisNote = convergence?.windAxis
    ? `wind axis · α=${aoa}°`
    : (aoa === 0 ? 'wind axis · α=0°' : 'body axis');

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{
        fontSize: 10, fontWeight: 700, letterSpacing: 0.8,
        color: '#22d3ee', textTransform: 'uppercase', marginBottom: 8,
      }}>
        Force Convergence (lift / drag vs timestep) <span style={{ color: '#6b7280', fontWeight: 500, textTransform: 'none' }}>— {axisNote}</span>
      </div>
      <div style={{
        backgroundColor: '#0b1322', borderRadius: 10,
        border: '1px solid #22d3ee33', padding: '12px 8px 4px', height: 240,
      }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 4, right: 12, bottom: 4, left: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
            <XAxis dataKey="ts" stroke="#6b7280" tick={{ fontSize: 10 }}
              tickFormatter={(t) => t.toLocaleString()} />
            <YAxis yAxisId="lift" stroke="#22d3ee" tick={{ fontSize: 10 }}
              width={54} tickFormatter={(v) => v.toFixed(3)} />
            <YAxis yAxisId="drag" orientation="right" stroke="#f59e0b" tick={{ fontSize: 10 }}
              width={54} tickFormatter={(v) => v.toFixed(3)} />
            <Tooltip contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', fontSize: 12 }}
              labelStyle={{ color: '#9ca3af' }} labelFormatter={(t) => `timestep ${Number(t).toLocaleString()}`} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line yAxisId="lift" type="monotone" dataKey="lift" name="Lift"
              stroke="#22d3ee" dot={false} strokeWidth={2} isAnimationActive={false} />
            <Line yAxisId="drag" type="monotone" dataKey="drag" name="Drag"
              stroke="#f59e0b" dot={false} strokeWidth={2} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// Lift-to-drag ratio over timestep — the headline aircraft efficiency metric,
// derived from the lift/drag convergence series. A flattening L/D means the
// aero solution has converged.
export function LiftDragRatioChart({ convergence, status }) {
  const { data, latest } = useMemo(() => {
    const lift = convergence?.lift || [];
    const drag = convergence?.drag || [];
    const byTs = new Map();
    lift.forEach(([ts, v]) => byTs.set(ts, { ts, lift: v }));
    drag.forEach(([ts, v]) => byTs.set(ts, { ...(byTs.get(ts) || { ts }), drag: v }));
    const rows = Array.from(byTs.values())
      .filter(r => r.lift != null && r.drag != null && Math.abs(r.drag) > 1e-9)
      .map(r => ({ ts: r.ts, ld: r.lift / r.drag }))
      .sort((a, b) => a.ts - b.ts);
    return { data: rows, latest: rows.length ? rows[rows.length - 1].ld : null };
  }, [convergence]);

  if (data.length < 2) return null;
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8,
      }}>
        <span style={{
          fontSize: 10, fontWeight: 700, letterSpacing: 0.8,
          color: '#a78bfa', textTransform: 'uppercase',
        }}>Lift / Drag Ratio (efficiency)</span>
        {convergence?.aoaDeg != null && (
          <span style={{ color: '#6b7280', fontSize: 10 }}>
            {convergence.windAxis || convergence.aoaDeg === 0 ? `wind axis · α=${convergence.aoaDeg}°` : 'body axis'}
          </span>
        )}
        {latest != null && (
          <span style={{ color: '#fff', fontSize: 13, fontFamily: 'monospace' }}>
            current L/D = <b style={{ color: '#a78bfa' }}>{latest.toFixed(2)}</b>
          </span>
        )}
        {status && <ConvergenceBadge status={status} />}
      </div>
      <div style={{
        backgroundColor: '#0b1322', borderRadius: 10,
        border: '1px solid #a78bfa33', padding: '12px 8px 4px', height: 200,
      }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 4, right: 12, bottom: 4, left: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
            <XAxis dataKey="ts" stroke="#6b7280" tick={{ fontSize: 10 }}
              tickFormatter={(t) => t.toLocaleString()} />
            <YAxis stroke="#a78bfa" tick={{ fontSize: 10 }} width={48}
              tickFormatter={(v) => v.toFixed(1)} />
            <Tooltip contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', fontSize: 12 }}
              labelStyle={{ color: '#9ca3af' }}
              labelFormatter={(t) => `timestep ${Number(t).toLocaleString()}`}
              formatter={(v) => [Number(v).toFixed(3), 'L/D']} />
            <Line type="monotone" dataKey="ld" name="L/D" stroke="#a78bfa"
              dot={false} strokeWidth={2} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// Generic single-series convergence trace ([ts, val] pairs) — used for side
// force and pitching moment, which each have their own magnitude scale.
export function SingleSeriesChart({ label, color, series, decimals = 4 }) {
  const { data, latest } = useMemo(() => {
    const rows = (series || [])
      .filter(pt => Array.isArray(pt) && pt.length === 2 && Number.isFinite(pt[1]))
      .map(([ts, v]) => ({ ts, v }))
      .sort((a, b) => a.ts - b.ts);
    return { data: rows, latest: rows.length ? rows[rows.length - 1].v : null };
  }, [series]);
  if (data.length < 2) return null;
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8 }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.8, color, textTransform: 'uppercase' }}>{label}</span>
        {latest != null && (
          <span style={{ color: '#fff', fontSize: 13, fontFamily: 'monospace' }}>
            current = <b style={{ color }}>{latest.toFixed(decimals)}</b>
          </span>
        )}
      </div>
      <div style={{ backgroundColor: '#0b1322', borderRadius: 10, border: `1px solid ${color}33`, padding: '12px 8px 4px', height: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 4, right: 12, bottom: 4, left: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
            <XAxis dataKey="ts" stroke="#6b7280" tick={{ fontSize: 10 }} tickFormatter={(t) => t.toLocaleString()} />
            <YAxis stroke={color} tick={{ fontSize: 10 }} width={60} tickFormatter={(v) => v.toFixed(decimals <= 2 ? decimals : 2)} />
            <Tooltip contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', fontSize: 12 }}
              labelStyle={{ color: '#9ca3af' }} labelFormatter={(t) => `timestep ${Number(t).toLocaleString()}`}
              formatter={(v) => [Number(v).toFixed(decimals), label]} />
            <Line type="monotone" dataKey="v" name={label} stroke={color} dot={false} strokeWidth={2} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// Per-surface drag/lift breakdown (from forces.csnc) — answers "where is the
// drag/lift coming from?" across the aircraft's named parts.
export function ForceBreakdownChart({ breakdown }) {
  const data = useMemo(() => (breakdown || []).slice(0, 12), [breakdown]);
  if (data.length === 0) return null;
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{
        fontSize: 10, fontWeight: 700, letterSpacing: 0.8,
        color: '#f59e0b', textTransform: 'uppercase', marginBottom: 8,
      }}>
        Force Breakdown by Surface (drag / lift)
      </div>
      <div style={{
        backgroundColor: '#0b1322', borderRadius: 10,
        border: '1px solid #f59e0b33', padding: '12px 8px 4px',
        height: Math.max(160, data.length * 34 + 40),
      }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" horizontal={false} />
            <XAxis type="number" stroke="#6b7280" tick={{ fontSize: 10 }} tickFormatter={(v) => v.toFixed(3)} />
            <YAxis type="category" dataKey="surface" stroke="#9ca3af" tick={{ fontSize: 11 }} width={110} />
            <Tooltip
              contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', fontSize: 12 }}
              labelStyle={{ color: '#fff' }}
              formatter={(v, n) => [Number(v).toFixed(4), n]}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="drag" name="Drag (Fx)" fill="#f59e0b" />
            <Bar dataKey="lift" name="Lift (Fz)" fill="#22d3ee" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// Multi-line probe chart: one line per monitoring point (wing tip/root, nacelle).
const PROBE_COLORS = ['#22d3ee', '#f59e0b', '#a78bfa', '#34d399', '#f472b6'];
export function PointProbeChart({ label, data, labels }) {
  const keys = useMemo(() => Object.keys(labels || {}), [labels]);
  if (!data || data.length < 2 || keys.length === 0) return null;
  return (
    <div className="report-block" style={{ marginTop: 14 }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.8, color: '#38bdf8', textTransform: 'uppercase', marginBottom: 8 }}>{label}</div>
      <div style={{ backgroundColor: '#0b1322', borderRadius: 10, border: '1px solid #38bdf833', padding: '12px 8px 4px', height: 230 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 4, right: 12, bottom: 4, left: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
            <XAxis dataKey="ts" stroke="#6b7280" tick={{ fontSize: 10 }} tickFormatter={(t) => t.toLocaleString()} />
            <YAxis stroke="#6b7280" tick={{ fontSize: 10 }} width={54} tickFormatter={(v) => v.toFixed(3)} />
            <Tooltip contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', fontSize: 12 }}
              labelStyle={{ color: '#9ca3af' }} labelFormatter={(t) => `timestep ${Number(t).toLocaleString()}`} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {keys.map((k, i) => (
              <Line key={k} type="monotone" dataKey={k} name={labels[k]} connectNulls
                stroke={PROBE_COLORS[i % PROBE_COLORS.length]} dot={false} strokeWidth={1.5} isAnimationActive={false} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// Spanwise load distribution from process_wing_rev09.sh outputs (Aero_Output.txt
// / Structure_Output.txt). Aero rows carry Cl/Cd/twist per % semi-span; structure
// rows carry full-scale lift/drag per station (the loads that feed the FEA hand-off).
export function SpanwiseChartBlock({ label, color, data, series, decimals = 4, unit = '' }) {
  if (!data || data.length < 2) return null;
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.8, color, textTransform: 'uppercase', marginBottom: 8 }}>{label}</div>
      <div style={{ backgroundColor: '#0b1322', borderRadius: 10, border: `1px solid ${color}33`, padding: '12px 8px 4px', height: 230 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 4, right: 12, bottom: 16, left: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
            <XAxis dataKey="station" type="number" domain={['dataMin', 'dataMax']} stroke="#6b7280"
              tick={{ fontSize: 10 }} tickFormatter={(v) => `${v.toFixed(0)}%`}
              label={{ value: '% semi-span (y/b)', position: 'insideBottom', offset: -6, fill: '#6b7280', fontSize: 10 }} />
            <YAxis stroke="#6b7280" tick={{ fontSize: 10 }} width={56}
              tickFormatter={(v) => Math.abs(v) >= 100 ? v.toFixed(0) : v.toFixed(decimals <= 2 ? decimals : 2)} />
            <Tooltip contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', fontSize: 12 }}
              labelStyle={{ color: '#9ca3af' }} labelFormatter={(v) => `${Number(v).toFixed(1)}% semi-span`}
              formatter={(v, n) => [`${Number(v).toFixed(decimals)}${unit}`, n]} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {series.map((s) => (
              <Line key={s.key} type="monotone" dataKey={s.key} name={s.name} stroke={s.color}
                dot={false} strokeWidth={1.75} isAnimationActive={false} connectNulls />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
